import React from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

interface SidebarProps {
  activeMenu: string;
  onMenuClick: (menu: string) => void;
  isOpen: boolean;
  onToggle: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ activeMenu, onMenuClick, isOpen, onToggle }) => {
  const { user, logout } = useAuth();
  
  const menuItems = [
    { id: 'home', label: 'Home', icon: '🏠' },
    { id: 'hottest-chains', label: 'Hottest Chains', icon: '🔥' },
    { id: 'option-flow', label: 'Option Flow Feed', icon: '📊' },
    { id: 'option-filter-flow', label: 'Option Filter Flow', icon: '🔍' },
    { id: 'option-historic', label: 'Option Historic Data', icon: '📈' },
    { id: 'unusual-flows', label: 'Unusual Flows', icon: '⚡' },
    { id: 'institutions', label: 'Institutions', icon: '🏦' },
    { id: 'news', label: 'News', icon: '📰' },
  ];

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const sidebarVariants = {
    open: { width: 250 },
    closed: { width: 60 }
  };

  const menuItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 }
  };

  return (
    <motion.div 
      className={`sidebar ${isOpen ? 'sidebar-open' : 'sidebar-collapsed'}`}
      variants={sidebarVariants}
      animate={isOpen ? "open" : "closed"}
      transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      <motion.div 
        className="sidebar-header"
        layout
        transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <motion.button
          onClick={onToggle}
          className="sidebar-hamburger"
          title={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          whileHover={{ 
            scale: 1.05,
            transition: { duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }
          }}
          whileTap={{ 
            scale: 0.95,
            transition: { duration: 0.1, ease: [0.25, 0.46, 0.45, 0.94] }
          }}
        >
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </motion.button>
        {isOpen && (
          <motion.h2
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            MIL
          </motion.h2>
        )}
      </motion.div>
      <nav className="sidebar-nav">
        {menuItems.map((item, index) => (
          <motion.button
            key={item.id}
            onClick={() => onMenuClick(item.id)}
            className={`sidebar-menu-item ${
              activeMenu === item.id ? 'sidebar-menu-item-active' : ''
            }`}
            title={!isOpen ? item.label : ''}
            variants={menuItemVariants}
            initial="hidden"
            animate="visible"
            transition={{ 
              delay: index * 0.05,
              duration: 0.25, 
              ease: [0.25, 0.46, 0.45, 0.94]
            }}
            whileHover={{ 
              scale: 1.02,
              transition: { duration: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }
            }}
            whileTap={{ 
              scale: 0.98,
              transition: { duration: 0.1, ease: [0.25, 0.46, 0.45, 0.94] }
            }}
          >
            <span className="sidebar-menu-icon">{item.icon}</span>
            {isOpen && (
              <motion.span 
                className="sidebar-menu-label"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                {item.label}
              </motion.span>
            )}
          </motion.button>
        ))}
      </nav>
      
      {/* User Profile and Logout */}
      <div className="sidebar-footer">
        {isOpen ? (
          <motion.div
            className="user-profile"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            <div className="user-info">
              <div className="user-icon">👤</div>
              <div className="user-details">
                <div className="user-name">{user?.username || 'User'}</div>
                <div className="user-email">{user?.email || 'Authenticated'}</div>
              </div>
            </div>
            <motion.button
              onClick={handleLogout}
              className="logout-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Logout"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>Logout</span>
            </motion.button>
          </motion.div>
        ) : (
          <motion.button
            onClick={handleLogout}
            className="logout-button-icon"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            title="Logout"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </motion.button>
        )}
      </div>
    </motion.div>
  );
};
